import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScriptBulkLogMessages, ScriptLogMessage } from "~/types/api";
import { ScriptLogCategoryEnum } from "~/utils/api-util";
import { Card, CardContent } from "../ui/card";
import { Checkbox, Label } from "../ui";
import { Badge } from "../ui/badge";
import { cc } from "~/utils/style-util";

export function ScriptLogs({
  logs,
}: {
  logs?: string | ScriptLogMessage[] | ScriptBulkLogMessages;
}) {
  const { t } = useTranslation();
  const [filterCategories, setFilterCategories] = useState<number[]>(
    Object.values(ScriptLogCategoryEnum),
  );

  if (logs == undefined) return <Label>{t("scripts.logs.empty")}</Label>;
  if (logs && typeof logs === "string") return <Label>{logs}</Label>;

  const [log, setLog] = useState<ScriptLogMessage[]>(
    (isBulkLogs(logs as ScriptLogMessage[] | ScriptBulkLogMessages)
      ? Object.values(logs).at(0)
      : logs) ?? {},
  );

  return (
    <div>
      <div className="flex">
        {isBulkLogs(logs as ScriptLogMessage[] | ScriptBulkLogMessages) ? (
          <ScriptIdLogSelector
            logs={logs as ScriptBulkLogMessages}
            setLog={(val) => setLog(val)}
          />
        ) : (
          <></>
        )}
        <div className="w-[100%]">
          <LogFilter
            filterCategories={filterCategories}
            setFilterCategories={(val) => setFilterCategories(val)}
          />

          <Card className="ml-2 h-[75vh] w-full min-w-[90%] p-1 shadow-md gap-1 overflow-x-hidden">
            <CardContent className="flex flex-col items-start gap-5">
              {log.length == 0 ? (
                <Label>{t("scripts.logs.empty")}</Label>
              ) : (
                log
                  .filter((x) => filterCategories.includes(x.category))
                  .map((entry, i) => (
                    <div key={i}>
                      <Badge
                        className={cc(
                          "mr-1 bg-gray-500/20",
                          entry.category == ScriptLogCategoryEnum.ERROR
                            ? "text-red-700"
                            : "text-green-700",
                        )}
                      >
                        {`${t(`scripts.logs.category.${entry.category}`)} `}
                      </Badge>
                      <Label>{entry.message}</Label>
                    </div>
                  ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function isBulkLogs(
  logs: ScriptLogMessage[] | ScriptBulkLogMessages,
): logs is ScriptBulkLogMessages {
  return logs && !Array.isArray(logs);
}

function ScriptIdLogSelector({
  logs,
  setLog,
}: {
  logs: ScriptBulkLogMessages;
  setLog: (log: ScriptLogMessage[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="h-[75vh] max-w-[10%] w-[10%] min-w-[10%] overflow-x-hidden gap-1">
      <div className="text-lg font-sm">{t("scripts.logs.scriptIds")}</div>
      <div className="flex flex-col items-start gap-1">
        {Object.entries(logs).map((kv) => (
          <Label
            className="hover:cursor-pointer"
            key={kv[0] + "-log-ids"}
            onClick={() => {
              setLog(kv[1]);
            }}
          >
            {kv[0]}
          </Label>
        ))}
      </div>
    </div>
  );
}

function LogFilter({
  filterCategories,
  setFilterCategories,
}: {
  filterCategories: number[];
  setFilterCategories: (filters: number[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex ml-4 mb-1 justify-start ">
      {Object.values(ScriptLogCategoryEnum).map((filterOption) => (
        <div className="mr-4" key={filterOption}>
          <Checkbox
            checked={
              filterCategories.filter((x) => x == filterOption).length > 0
            }
            onCheckedChange={(checked) =>
              setFilterCategories(
                (checked.valueOf() as boolean)
                  ? filterCategories.concat(filterOption)
                  : filterCategories.filter((x) => x != filterOption),
              )
            }
          />
          <Badge
            className={cc(
              "ml-1  bg-gray-500/20",
              filterOption == ScriptLogCategoryEnum.ERROR
                ? "text-red-700"
                : "text-green-700",
            )}
          >
            {t(`scripts.logs.category.${filterOption}`)}
          </Badge>
        </div>
      ))}
    </div>
  );
}
